/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import {
  genericActions,
  genericSelectors,
  layerActions,
  mapActions,
  uiSelectors,
  uiTypes,
  useSetupDialog,
} from '@opengeoweb/store';
import type { CoreAppStore, syncGroupsTypes } from '@opengeoweb/store';
import type { MapLocation } from '@opengeoweb/webmap-react';
import { timeSeriesActions } from '../../../store';
import {
  getCurrentLocation,
  getGeoJSONWithService,
  getIsMapPinDisabled,
} from '../../../store/selectors';
import type { TimeSeriesRootState } from '../../../store/types';
import { getFeatureCenterPoint } from '../../../utils/edrUtils';
import { useTimeseriesTranslation } from '../../../utils/i18n';
import { LocationDropdown } from '../LocationDropdown/LocationDropdown';
import type { TimeSeriesLocation } from '../types';

const emptyMapIdList: string[] = [];

export const LocationSelectDialogConnect: React.FC = () => {
  const { t } = useTimeseriesTranslation();
  const dispatch = useDispatch();

  const panelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(
      store,
      uiTypes.DialogTypes.TimeseriesLocations,
    ),
  );
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.TimeseriesLocations);

  const geoJSONWithService = useSelector((state: TimeSeriesRootState) =>
    panelId ? getGeoJSONWithService(state, panelId) : undefined,
  );
  const sharedGeoJSON = useSelector((state: TimeSeriesRootState) =>
    panelId ? genericSelectors.selectSharedData(state, panelId) : undefined,
  );
  const selectedMapIds = useSelector((state: CoreAppStore) =>
    panelId
      ? genericSelectors.getLinkedTargetIds(state, panelId)
      : emptyMapIdList,
  );
  const isMapPinDisabled = useSelector((state: TimeSeriesRootState) =>
    panelId ? getIsMapPinDisabled(state, panelId) : false,
  );

  const timeSeriesLocation = useSelector((state: TimeSeriesRootState) =>
    panelId ? getCurrentLocation(state, panelId) : undefined,
  );

  const setSelectedFeatureIndex = (selectedFeatureIndex: number): void => {
    dispatch(
      layerActions.setSelectedFeature({
        layerId: geojsonLayerId!,
        selectedFeatureIndex,
      }),
    );
  };

  const handleFeatureSelect = (panelId: string, featureId: string): void => {
    dispatch(
      genericActions.addSharedData({
        panelId,
        data: { selectedFeatureId: featureId || '' },
      }),
    );
  };

  const setTimeSeriesLocation = useCallback(
    (location: TimeSeriesLocation | undefined): void => {
      if (!panelId) {
        return;
      }
      dispatch(timeSeriesActions.setCurrentLocation({ panelId, location }));
    },
    [dispatch, panelId],
  );

  const setMapPinLocation = (mapPinLocation: MapLocation): void => {
    selectedMapIds.forEach((mapId) => {
      dispatch(
        mapActions.setMapPinLocation({
          mapId,
          mapPinLocation,
        }),
      );
    });
  };

  const sharedFeature = Object.values(
    sharedGeoJSON?.featureLayersById ?? {},
  )[0];

  const geojson = sharedFeature?.geoJSON;
  const geojsonLayerId = sharedFeature?.layerId;
  const selectedLocationName =
    typeof timeSeriesLocation?.occurrences?.[0]?.properties?.name === 'string'
      ? timeSeriesLocation.occurrences[0].properties.name
      : '';

  const onChangeLocation = (newSelectedLocation: string): void => {
    if (!panelId) {
      return;
    }
    if (!geojson?.features?.length) {
      return;
    }

    const selectedFeatureIndex = geojson.features.findIndex((feature) =>
      Object.keys(feature.properties!.names!).includes(newSelectedLocation),
    );
    setSelectedFeatureIndex(selectedFeatureIndex);

    const feature = geojson.features[selectedFeatureIndex];
    const [lon, lat] = getFeatureCenterPoint(feature);
    const location: TimeSeriesLocation = {
      lat,
      lon,
      id: feature.id as string,
      occurrences: feature.properties?.occurrences?.map(
        (occ: syncGroupsTypes.Occurrence) => {
          return {
            id: occ.id,
            properties: {
              name: occ.properties?.name,
              serviceId: occ.properties?.serviceId,
              collectionId: occ.properties?.collectionId,
              parameterName: occ.properties?.parameterName,
            },
          };
        },
      ),
    };

    handleFeatureSelect(panelId, feature.id as string);
    // handle markers
    setTimeSeriesLocation(location);
    const { occurrences, ...newMapPin } = location;
    setMapPinLocation(newMapPin);
  };

  if (!panelId) {
    return null;
  }

  return (
    <ToolContainerDraggable
      bounds="parent"
      isOpen={isDialogOpen}
      minWidth={264}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      order={dialogOrder}
      startPosition={{ top: 60, left: 200 }}
      startSize={{ width: 320, height: 500 }}
      title={t('locations')}
    >
      <LocationDropdown
        geoJson={geojson!}
        handleChange={onChangeLocation}
        isDisabled={isMapPinDisabled}
        selectedLocation={selectedLocationName}
        splitGeoJson={geoJSONWithService!}
      />
    </ToolContainerDraggable>
  );
};
